import { Slot } from 'radix-ui';
import * as React from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import { cn } from '../../lib/utils';

/**
 * Form — Generated from Figma Nimbus v3 Design System
 * @figma-node 477:8653
 * @figma-url https://www.figma.com/design/euN3FdxtNlCC8GiKC66YmG/Nimbus-v3--TBD-?node-id=477-8653
 *
 * Variants: Breakpoint (Desktop / Mobile)
 *
 * This module follows the shadcn/react-hook-form pattern:
 * - Form: context provider wrapping react-hook-form's FormProvider
 * - FormField: connects a form field to react-hook-form via Controller
 * - FormItem: layout wrapper for label + control + description + error
 * - FormLabel: label element styled per Nimbus tokens
 * - FormControl: slot wrapper for the actual input/control
 * - FormDescription: helper text below the control
 * - FormMessage: validation error message
 *
 * Typography from Figma:
 *   Label:       fontSize=14, fontWeight=500, lineHeight=14, color=#202020 (nimbus-foreground)
 *   Description: fontSize=14, fontWeight=400, lineHeight=20, color=#727272 (nimbus-muted-foreground)
 *   Error:       fontSize=14, fontWeight=400, lineHeight=20, color=#db2929 / dark:#f8a79d
 */

/* ─── Form (context provider) ────────────────────────────────────────────── */

const Form = FormProvider;

/* ─── FormField context ──────────────────────────────────────────────────── */

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

/* ─── FormItem context ───────────────────────────────────────────────────── */

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

/* ─── useFormField hook ──────────────────────────────────────────────────── */

function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

/* ─── FormItem ───────────────────────────────────────────────────────────── */

/**
 * Layout wrapper for a single form field.
 * Figma: vertical layout, gap=8 between label/input/description
 */
function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn(
          /* Figma: VERTICAL gap=8 (gap between label, input, description) */
          'flex flex-col gap-2',
          className
        )}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

/* ─── FormLabel ──────────────────────────────────────────────────────────── */

/**
 * Label for a form field.
 * Figma: fontSize=14, fontWeight=500, lineHeight=14, color=#202020 (nimbus-foreground)
 */
function FormLabel({ className, ...props }: React.ComponentProps<'label'>) {
  const { error, formItemId } = useFormField();

  return (
    <label
      data-slot="form-label"
      htmlFor={formItemId}
      className={cn(
        /*
         * Figma: fontSize=14, fontWeight=500, lineHeight=14
         * Color: #202020 (nimbus-foreground)
         * Error: #db2929 / dark:#f8a79d
         */
        'text-sm font-medium leading-[14px] text-nimbus-foreground',
        error && 'text-nimbus-destructive',
        className
      )}
      {...props}
    />
  );
}

/* ─── FormControl ────────────────────────────────────────────────────────── */

/**
 * Slot wrapper for the actual input/control element.
 * Passes through aria attributes for accessibility.
 */
function FormControl({ ...props }: React.ComponentProps<typeof Slot.Root>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot.Root
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
}

/* ─── FormDescription ────────────────────────────────────────────────────── */

/**
 * Helper text below the control.
 * Figma: fontSize=14, fontWeight=400, lineHeight=20, color=#727272 (nimbus-muted-foreground)
 */
function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn(
        /*
         * Figma: fontSize=14, fontWeight=400, lineHeight=20
         * Color: #727272 (nimbus-muted-foreground)
         */
        'text-sm leading-5 text-nimbus-muted-foreground',
        className
      )}
      {...props}
    />
  );
}

/* ─── FormMessage ────────────────────────────────────────────────────────── */

/**
 * Validation error message.
 * Figma: same typography as description but in destructive color.
 * Color: #db2929 / dark:#f8a79d
 */
function FormMessage({
  className,
  children,
  ...props
}: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn(
        /*
         * Figma: fontSize=14, fontWeight=400, lineHeight=20
         * Color: #db2929 (destructive) / dark: #f8a79d
         */
        'text-sm leading-5 text-nimbus-destructive',
        className
      )}
      {...props}
    >
      {body}
    </p>
  );
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
